import { useState, useEffect, useMemo } from "react";
import { useContent } from "@/content";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldAlert, ArrowLeft, Trophy, AlertTriangle, CheckCircle2, XCircle, RotateCcw, Hourglass, HelpCircle, Check, X, MessageCircle, Download, Loader2 } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function SecureExamResult() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { ct } = useContent();
  const { user, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selSec, setSelSec] = useState(0);
  const [savingPdf, setSavingPdf] = useState(false);

  const isTeacher = user?.role === "teacher" || user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const url = isTeacher ? `${API}/secure-exams/${examId}/results` : `${API}/secure-exams/${examId}/my-result`;
        const r = await axios.get(url, { headers });
        setData(r.data);
      } catch (e) { /* handled below */ }
      finally { setLoading(false); }
    })();
  }, [examId, isTeacher, headers]);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading result...</div>;
  if (!data) return (
    <div className="p-8 text-center">
      <p className="text-sm text-slate-500 mb-3">Result not available yet.</p>
      <Button onClick={() => navigate("/secure-exams")}>Back</Button>
    </div>
  );

  // TEACHER VIEW — leaderboard
  if (isTeacher) {
    const attempts = data.attempts || [];
    const absent = data.absent || [];
    const toggleRanks = async () => {
      try {
        const enabled = !data.ranks_released;
        await axios.post(`${API}/secure-exams/${examId}/release-ranks`, { enabled }, { headers });
        setData(prev => ({ ...prev, ranks_released: enabled }));
        toast.success(enabled ? "Ranks released — students can now see their class rank" : "Ranks hidden from students");
      } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update ranks"); }
    };
    const downloadScoreboardPdf = () => {
      setSavingPdf(true);
      try {
        const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        const margin = 12;
        const cols = [
          { t: "Rank", x: margin + 2, w: 14 },
          { t: "Student", x: margin + 16, w: 60 },
          { t: "Score", x: margin + 78, w: 26 },
          { t: "%", x: margin + 104, w: 14 },
          { t: "Status", x: margin + 118, w: 30 },
          { t: "Submitted", x: margin + 148, w: 38 }
        ];
        const brandHeader = () => {
          pdf.setFillColor(15, 27, 71);
          pdf.rect(0, 0, pw, 24, "F");
          pdf.setTextColor(212, 162, 76);
          pdf.setFontSize(14);
          pdf.setFont("helvetica", "bold");
          pdf.text("TheMatheMind 360", margin, 10);
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(10);
          pdf.text(`${ct("olympiad_name")} Class Scoreboard`, margin, 16);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(9);
          pdf.text(new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }), pw - margin, 10, { align: "right" });
          pdf.text(data.exam.title || "", pw - margin, 16, { align: "right" });
        };
        const tableHead = (y) => {
          pdf.setFillColor(241, 245, 249);
          pdf.rect(margin, y - 4.5, pw - margin * 2, 7, "F");
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(71, 85, 105);
          cols.forEach(c => pdf.text(c.t, c.x, y));
          pdf.setFont("helvetica", "normal");
          return y + 7;
        };
        brandHeader();
        pdf.setTextColor(51, 65, 85);
        pdf.setFontSize(9);
        pdf.text(`${attempts.length} attempted  ·  ${absent.length} not attempted  ·  Ranks ${data.ranks_released ? "released to" : "hidden from"} students`, margin, 31);
        let y = tableHead(39);
        pdf.setFontSize(9);
        attempts.forEach((a, i) => {
          if (y > ph - 22) { pdf.addPage(); brandHeader(); y = tableHead(33); pdf.setFontSize(9); }
          if (i % 2 === 1) { pdf.setFillColor(248, 250, 252); pdf.rect(margin, y - 4.5, pw - margin * 2, 7, "F"); }
          const pctv = a.total_marks ? Math.round((a.score / a.total_marks) * 100) : 0;
          pdf.setTextColor(a.rank === 1 ? 180 : 51, a.rank === 1 ? 130 : 65, a.rank === 1 ? 20 : 85);
          pdf.setFont("helvetica", a.rank === 1 ? "bold" : "normal");
          pdf.text(`#${a.rank}`, cols[0].x, y);
          pdf.text((a.student_name || "").slice(0, 34), cols[1].x, y);
          pdf.text(`${a.score ?? 0} / ${a.total_marks ?? 0}`, cols[2].x, y);
          pdf.text(`${pctv}%`, cols[3].x, y);
          pdf.text((a.status || "").replace("_", " "), cols[4].x, y);
          pdf.text(a.submitted_at ? new Date(a.submitted_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—", cols[5].x, y);
          pdf.setFont("helvetica", "normal");
          y += 7;
        });
        if (absent.length > 0) {
          if (y > ph - 30) { pdf.addPage(); brandHeader(); y = 33; }
          y += 4;
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(9);
          pdf.setTextColor(71, 85, 105);
          pdf.text(`Not attempted (${absent.length})`, margin, y);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(100, 116, 139);
          y += 5.5;
          const names = absent.map(s => s.student_name).join(", ");
          const wrapped = pdf.splitTextToSize(names, pw - margin * 2);
          wrapped.forEach(line => {
            if (y > ph - 15) { pdf.addPage(); brandHeader(); y = 33; }
            pdf.text(line, margin, y);
            y += 5;
          });
        }
        const pages = pdf.getNumberOfPages();
        for (let p = 1; p <= pages; p++) {
          pdf.setPage(p);
          pdf.setTextColor(120, 120, 120);
          pdf.setFontSize(7.5);
          pdf.text(`Generated by TheMatheMind 360 — Where Learning Meets Innovation  ·  Page ${p} of ${pages}`, pw / 2, ph - 5, { align: "center" });
        }
        const safeTitle = (data.exam.title || "Exam").replace(/[^a-z0-9]+/gi, "-").slice(0, 40);
        pdf.save(`Scoreboard-${safeTitle}-${new Date().toISOString().slice(0, 10)}.pdf`);
        toast.success("Scoreboard PDF downloaded");
      } catch (e) {
        console.error(e);
        toast.error("Couldn't generate PDF");
      } finally { setSavingPdf(false); }
    };
    return (
      <div className="min-h-screen bg-background">
        <div className="border-b border-border bg-slate-950 text-white px-4 py-4">
          <div className="max-w-5xl mx-auto flex items-center gap-3 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => navigate("/secure-exams")} className="text-white hover:bg-slate-800"><ArrowLeft className="w-4 h-4" /></Button>
            <ShieldAlert className="w-6 h-6 text-amber-400" />
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold">{data.exam.title} — Results</h1>
              <p className="text-[11px] text-slate-400">{attempts.length} attempted · {absent.length} not attempted</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-wide ${data.ranks_released ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-300"}`} data-testid="ranks-status-pill">
                {data.ranks_released ? "Ranks visible to students" : "Ranks hidden from students"}
              </span>
              <Button size="sm" variant="outline" onClick={downloadScoreboardPdf} disabled={savingPdf || attempts.length === 0}
                className="border-slate-600 bg-slate-800 text-white hover:bg-slate-700 hover:text-white"
                data-testid="download-scoreboard-btn">
                {savingPdf ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}PDF
              </Button>
              <Button size="sm" onClick={toggleRanks}
                className={data.ranks_released ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"}
                data-testid="release-ranks-btn">
                <Trophy className="w-3.5 h-3.5 mr-1.5" />{data.ranks_released ? "Hide Ranks" : "Release Ranks to Students"}
              </Button>
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-4">
          {attempts.length === 0 ? <p className="text-sm text-slate-500 text-center py-10">No submissions yet.</p> : (
            <Card className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b border-border">
                  <tr>
                    <th className="text-left py-2">Rank</th>
                    <th className="text-left py-2">Student</th>
                    <th className="text-left py-2">Score</th>
                    <th className="text-left py-2">%</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Warnings</th>
                    <th className="text-left py-2">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((a) => {
                    const pct = a.total_marks ? Math.round((a.score / a.total_marks) * 100) : 0;
                    const warns = Object.values(a.warnings || {}).reduce((s, v) => s + v, 0);
                    return (
                      <tr key={a.attempt_id} className="border-b border-border last:border-0" data-testid={`result-row-${a.attempt_id}`}>
                        <td className="py-2" data-testid={`rank-cell-${a.attempt_id}`}>
                          {a.rank === 1 ? (
                            <span className="inline-flex items-center gap-1 font-bold text-yellow-600"><Trophy className="w-4 h-4 text-yellow-500" />1</span>
                          ) : <span className="font-semibold text-slate-700">{a.rank}</span>}
                        </td>
                        <td className="py-2 font-medium">{a.student_name}</td>
                        <td className="py-2 font-mono">{a.score ?? 0} / {a.total_marks ?? 0}</td>
                        <td className="py-2">{pct}%</td>
                        <td className="py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === "submitted" ? "bg-green-100 text-green-700" : a.status === "auto_submitted" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                            {a.status}
                          </span>
                        </td>
                        <td className="py-2 text-center">{warns > 0 ? <span className="text-red-600 font-semibold">{warns}</span> : "—"}</td>
                        <td className="py-2 text-xs text-slate-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
          {absent.length > 0 && (
            <Card className="p-4" data-testid="absent-list">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Not attempted ({absent.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {absent.map(s => (
                  <span key={s.student_id} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid={`absent-${s.student_id}`}>{s.student_name}</span>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // STUDENT VIEW
  const score = data.score ?? 0;
  const total = data.total_marks ?? 0;
  const pct = total ? Math.round((score / total) * 100) : 0;
  const warns = Object.entries(data.warnings || {}).filter(([, v]) => v > 0);
  const correctMap = data.correct_map || {};
  const entries = Object.values(correctMap);
  const correctCount = entries.filter(x => x.is_correct).length;
  const total_qs = entries.length;
  const attemptedCount = entries.filter(x => x.student_answer !== "" && x.student_answer !== null && x.student_answer !== undefined).length;
  const incorrectCount = attemptedCount - correctCount;
  const unattempted = total_qs - attemptedCount;
  const accuracy = attemptedCount ? Math.round((correctCount / attemptedCount) * 100) : 0;
  const isPractice = !!data.practice;

  const durationSec = (data.started_at && data.submitted_at)
    ? Math.max(0, Math.floor((new Date(data.submitted_at) - new Date(data.started_at)) / 1000)) : null;
  const timeText = durationSec !== null ? `${Math.floor(durationSec / 60)} minutes, ${durationSec % 60} seconds` : null;

  const sections = data.sections || [];
  const sectionStats = sections.map((s, i) => {
    const secIdx = s.index ?? i;
    const qs = entries.filter(x => (x.section_index ?? 0) === secIdx);
    const c = qs.filter(x => x.is_correct).length;
    const att = qs.filter(x => x.student_answer !== "" && x.student_answer !== null && x.student_answer !== undefined).length;
    return {
      name: s.name,
      correct: c,
      incorrect: att - c,
      unattempted: qs.length - att,
      totalQs: qs.length,
      score: qs.filter(x => x.is_correct).reduce((sum, x) => sum + (Number(x.marks) || 0), 0),
      totalMarks: qs.reduce((sum, x) => sum + (Number(x.marks) || 0), 0),
      accuracy: att ? Math.round((c / att) * 100) : 0
    };
  });
  const sec = sectionStats[Math.min(selSec, Math.max(0, sectionStats.length - 1))];

  const shareOnWhatsApp = () => {
    const lines = [
      `*${data.exam_title}* - My Result`,
      ``,
      `Overall Score: *${score} / ${total}* (${pct}%)`,
      `Accuracy: ${accuracy}%`
    ];
    if (timeText) lines.push(`Time Taken: ${timeText}`);
    lines.push(`Correct: ${correctCount} | Incorrect: ${incorrectCount} | Unattempted: ${unattempted}`);
    if (sectionStats.length > 0) {
      lines.push(``, `*Section-wise Scores:*`);
      sectionStats.forEach((st, i) => {
        lines.push(`${i + 1}. ${st.name}: *${st.score}/${st.totalMarks}* (Correct ${st.correct}, Incorrect ${st.incorrect}, Unattempted ${st.unattempted})`);
      });
    }
    lines.push(``, `- TheMatheMind 360 ${ct("olympiad_name")}`);
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener,noreferrer");
  };

  const downloadPdf = async () => {
    setSavingPdf(true);
    try {
      const node = document.getElementById("result-printable");
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Brand header
      pdf.setFillColor(15, 27, 71);
      pdf.rect(0, 0, pageWidth, 24, "F");
      pdf.setTextColor(212, 162, 76);
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text("TheMatheMind 360", margin, 10);
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.text(`${ct("olympiad_name")} Mark Sheet`, margin, 16);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(`${user?.name || "Student"} · ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`, pageWidth - margin, 10, { align: "right" });
      pdf.text(data.exam_title || "", pageWidth - margin, 16, { align: "right" });

      let y = 28;
      const pageContentHeight = pageHeight - y - margin;
      if (imgHeight <= pageContentHeight) {
        pdf.addImage(imgData, "PNG", margin, y, imgWidth, imgHeight);
      } else {
        let remainingHeight = imgHeight;
        let sourceY = 0;
        while (remainingHeight > 0) {
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          const sliceHeight = Math.min(canvas.height - sourceY, (pageContentHeight * canvas.width) / imgWidth);
          sliceCanvas.height = sliceHeight;
          sliceCanvas.getContext("2d").drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
          const sliceMmHeight = (sliceHeight * imgWidth) / canvas.width;
          pdf.addImage(sliceCanvas.toDataURL("image/png"), "PNG", margin, y, imgWidth, sliceMmHeight);
          remainingHeight -= sliceMmHeight;
          sourceY += sliceHeight;
          if (remainingHeight > 0) { pdf.addPage(); y = margin; }
        }
      }
      // Footer
      pdf.setTextColor(120, 120, 120);
      pdf.setFontSize(7.5);
      pdf.text("Generated by TheMatheMind 360 — Where Learning Meets Innovation", pageWidth / 2, pageHeight - 5, { align: "center" });

      const safeTitle = (data.exam_title || "Result").replace(/[^a-z0-9]+/gi, "-").slice(0, 40);
      pdf.save(`MarkSheet-${safeTitle}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error(e);
    } finally { setSavingPdf(false); }
  };

  const statRows = [
    { label: "Total Questions", value: total_qs, bg: "bg-purple-500", icon: <HelpCircle className="w-4 h-4 text-white" />, testid: "stat-total" },
    { label: "Correct Answers", value: correctCount, bg: "bg-green-500", icon: <Check className="w-4 h-4 text-white" />, testid: "stat-correct" },
    { label: "Incorrect Answers", value: incorrectCount, bg: "bg-red-500", icon: <X className="w-4 h-4 text-white" />, testid: "stat-incorrect" },
    { label: "Partially Correct Answers", value: 0, bg: "bg-orange-400", icon: <span className="text-white font-bold text-sm leading-none">~</span>, testid: "stat-partial" },
    { label: "Unattempted Questions", value: unattempted, bg: "bg-slate-400", icon: <span className="text-white font-bold text-sm leading-none">!</span>, testid: "stat-unattempted" }
  ];

  const C = 2 * Math.PI * 40;
  const barMax = Math.max(sec?.correct || 0, sec?.incorrect || 0, sec?.unattempted || 0, 1);
  const bars = sec ? [
    { label: "Correct", value: sec.correct, color: "bg-green-500", text: "text-green-600" },
    { label: "Incorrect", value: sec.incorrect, color: "bg-red-500", text: "text-red-600" },
    { label: "Partially Correct", value: 0, color: "bg-orange-400", text: "text-orange-500" },
    { label: "Unattempted", value: sec.unattempted, color: "bg-slate-400", text: "text-slate-500" }
  ] : [];

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-slate-950 text-white px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/student")} className="text-white hover:bg-slate-800"><ArrowLeft className="w-4 h-4" /></Button>
          <ShieldAlert className="w-6 h-6 text-amber-400" />
          <h1 className="text-lg font-bold flex items-center gap-2">
            {data.exam_title} — Your Result
            {isPractice && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-bold uppercase">Practice</span>}
          </h1>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-4 md:p-6 space-y-4">
        <div id="result-printable" className="space-y-4">
        {/* Score card */}
        <Card className="p-6" data-testid="result-score-card">
          <div className="text-center">
            <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Score</div>
            <div className="text-6xl font-black text-sky-600 leading-none mt-3 mb-3" data-testid="overall-score">{score}</div>
            <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Out of {total}</div>
            {timeText && (
              <div className="flex items-center justify-center gap-1.5 mt-3 text-sm text-slate-600" data-testid="time-taken">
                <Hourglass className="w-4 h-4 text-orange-500" />{timeText}
              </div>
            )}
            <div className="mt-4">
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden max-w-md mx-auto">
                <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${accuracy}%` }} />
              </div>
              <div className="text-xs tracking-[0.25em] uppercase text-slate-600 font-semibold mt-2" data-testid="overall-accuracy">{accuracy}% Accuracy</div>
            </div>
            {data.rank && (
              <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 text-sm font-bold text-amber-800" data-testid="class-rank-chip">
                <Trophy className="w-4 h-4 text-amber-500" />Class Rank: #{data.rank} of {data.rank_total}
              </div>
            )}
            {isPractice && (
              <div className="mt-3 inline-flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1 text-xs text-slate-700" data-testid="best-score-chip">
                <Trophy className="w-3.5 h-3.5 text-yellow-500" />Attempt #{data.attempt_number || 1} · Best score: {data.best_score ?? score}
              </div>
            )}
          </div>

          {/* Stat rows */}
          <div className="mt-6 space-y-4 max-w-md mx-auto">
            {statRows.map((r) => (
              <div key={r.label} className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full ${r.bg} flex items-center justify-center shrink-0`}>{r.icon}</div>
                <div className="flex-1 text-sm text-slate-700">{r.label}</div>
                <div className="text-base font-bold text-slate-900" data-testid={r.testid}>{r.value}</div>
              </div>
            ))}
          </div>

          <Button
            onClick={shareOnWhatsApp}
            className="w-full h-12 rounded-lg mt-6 bg-[#25D366] hover:bg-[#1EA952] text-white font-semibold text-base"
            data-testid="share-whatsapp-btn"
            data-html2canvas-ignore="true"
          >
            <MessageCircle className="w-5 h-5 mr-2" />Share on WhatsApp
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={savingPdf}
            variant="outline"
            className="w-full h-12 rounded-lg mt-2 border-slate-300 text-slate-700 font-semibold"
            data-testid="download-pdf-btn"
            data-html2canvas-ignore="true"
          >
            {savingPdf ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
            {savingPdf ? "Preparing Mark Sheet..." : "Download PDF Mark Sheet"}
          </Button>
        </Card>

        {/* Sectional summary */}
        {sectionStats.length > 0 && (
          <Card className="p-5" data-testid="sectional-summary">
            <div className="text-xs tracking-[0.25em] uppercase text-slate-500 font-bold mb-3">Sectional Summary</div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4">
              {sectionStats.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setSelSec(i)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${i === selSec ? "bg-sky-100 text-sky-700 font-semibold" : "text-slate-500 hover:bg-slate-100"}`}
                  data-testid={`section-summary-tab-${i}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
            {sec && (
              <div className="grid grid-cols-1 sm:grid-cols-[auto_auto_1fr] gap-5 items-center">
                <div className="text-center sm:px-2">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Score</div>
                  <div className="text-4xl font-black text-slate-900 leading-none mt-2 mb-2" data-testid="section-score">{sec.score}</div>
                  <div className="text-[10px] tracking-widest uppercase text-slate-500">Out of {sec.totalMarks}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Accuracy</div>
                  <div className="relative inline-block">
                    <svg width="96" height="96" viewBox="0 0 96 96">
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#22c55e" strokeWidth="10" strokeLinecap="round"
                        strokeDasharray={`${(sec.accuracy / 100) * C} ${C}`} transform="rotate(-90 48 48)" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center text-base font-bold text-slate-800" data-testid="section-accuracy">{sec.accuracy}%</div>
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-end justify-center gap-2 sm:gap-3 h-28">
                    {bars.map((b) => (
                      <div key={b.label} className="flex flex-col items-center justify-end h-full min-w-0">
                        <div className={`text-xs font-bold ${b.text} mb-1`}>{b.value}</div>
                        <div className={`w-7 sm:w-8 rounded-t ${b.value > 0 ? b.color : "bg-slate-100 border border-slate-200"}`} style={{ height: `${Math.max((b.value / barMax) * 66, 4)}px` }} />
                        <div className="text-[7px] uppercase text-slate-500 mt-1 text-center leading-tight w-14 tracking-tight">{b.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}
        </div>

        {isPractice && (
          <Button
            onClick={() => navigate(`/secure-exam/${examId}/take`)}
            className="w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
            data-testid="retake-test-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />Retake Test
          </Button>
        )}

        {warns.length > 0 && (
          <Card className="p-4 border-amber-300 bg-amber-50">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-amber-900">Proctoring Warnings</div>
                <div className="text-xs text-amber-800 space-y-0.5 mt-1">
                  {warns.map(([k, v]) => <div key={k}>• {k.replace("_", " ")}: {v}</div>)}
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-4">
          <div className="text-sm font-semibold mb-3">Question-wise Review</div>
          {(data.show_review || isPractice) ? (
            <div className="space-y-3" data-testid="practice-review-list">
              {Object.entries(correctMap).map(([qid, info], i) => (
                <div key={qid} className={`rounded-lg border p-3 ${info.is_correct ? "border-green-200 bg-green-50/50" : info.student_answer ? "border-red-200 bg-red-50/50" : "border-slate-200 bg-slate-50"}`} data-testid={`review-q-${i}`}>
                  <div className="flex items-start gap-2">
                    {info.is_correct ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" /> : info.student_answer ? <XCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /> : <span className="text-slate-400 text-xs mt-0.5 shrink-0">—</span>}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800">Q{i + 1}. {info.question_text}</div>
                      <div className="text-xs mt-1.5 space-y-0.5">
                        <div className={info.is_correct ? "text-green-700" : "text-red-700"}>
                          Your answer: <strong>{info.student_answer_text || info.student_answer || "Not attempted"}</strong>
                        </div>
                        {!info.is_correct && (
                          <div className="text-green-700">Correct answer: <strong>{info.correct_answer_text ?? info.correct_answer}</strong></div>
                        )}
                      </div>
                      {info.solution && (
                        <div className="mt-2 bg-sky-50 border border-sky-200 rounded-md p-2.5 text-xs text-sky-900" data-testid={`solution-q-${i}`}>
                          <span className="font-semibold">💡 Solution: </span>{info.solution}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-6 md:grid-cols-10 gap-1.5">
                {Object.entries(correctMap).map(([qid, info], i) => (
                  <div key={qid} className={`aspect-square rounded flex items-center justify-center text-xs font-bold ${info.is_correct ? "bg-green-100 text-green-800" : info.student_answer ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-500"}`} title={info.is_correct ? "Correct" : info.student_answer ? "Wrong" : "Not attempted"}>
                    {info.is_correct ? <CheckCircle2 className="w-4 h-4" /> : info.student_answer ? <XCircle className="w-4 h-4" /> : "—"}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-600">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-600" />Correct</span>
                <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-600" />Wrong</span>
                <span className="flex items-center gap-1">— Not attempted</span>
              </div>
            </>
          )}
        </Card>

        <div className="text-center">
          <Button variant="outline" onClick={() => navigate("/secure-exams")}>Back to Exams</Button>
        </div>
      </div>
    </div>
  );
}
